import { TypedDocumentNode, gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { Outlet, useLocation } from 'react-router-dom';

import { catchAll } from '@sorare/routing';

import { MyClubPageHeader } from '@sorare/core/src/components/navigation/MyClubPageHeader';
import {
  ANY_SPORT_MY_CLUB_SLUG_CARDS,
  ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS,
  ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTIONSLUG,
  ANY_SPORT_MY_CLUB_SLUG_HISTORY,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { fantasy, galleryTabs, navLabels } from '@sorare/core/src/lib/glossary';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import {
  MyClubSlugLayoutQuery,
  MyClubSlugLayoutQueryVariables,
} from './__generated__/index.graphql';

const MY_CLUB_SLUG_LAYOUT_QUERY = gql`
  query MyClubSlugLayoutQuery($slug: String!) {
    user(slug: $slug) {
      slug
      ...MyClubPageHeader_user
    }
  }
  ${MyClubPageHeader.fragments.user}
` as TypedDocumentNode<MyClubSlugLayoutQuery, MyClubSlugLayoutQueryVariables>;

const SUBTITLE_MAP = {
  [ANY_SPORT_MY_CLUB_SLUG_HISTORY]: fantasy.proHistory,
  [ANY_SPORT_MY_CLUB_SLUG_CARDS]: galleryTabs.cards,
  [ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS]: navLabels.collections,
};

export const loaderFn = <Params extends { slug: string }>() =>
  queryLoader<Params>(MY_CLUB_SLUG_LAYOUT_QUERY, ({ slug }) => ({
    slug,
  }));

export const MyClubSlugLayout = () => {
  const {
    flags: { useNewNav = false },
  } = useFeatureFlags();
  const { data } = useQueryLoader<MyClubSlugLayoutQuery>();
  const { matchAnySportPath } = useSportContext();
  const location = useLocation();
  const subtitleMessage = Object.entries(SUBTITLE_MAP).find(([key]) =>
    matchAnySportPath(catchAll(key), location.pathname)
  )?.[1];

  return (
    <>
      {!useNewNav &&
        !matchAnySportPath(
          catchAll(ANY_SPORT_MY_CLUB_SLUG_COLLECTIONS_COLLECTIONSLUG),
          location.pathname
        ) && (
          <MyClubPageHeader
            user={data.user}
            subtitle={
              subtitleMessage && <FormattedMessage {...subtitleMessage} />
            }
          />
        )}
      <Outlet />
    </>
  );
};
